import Button from '@atlaskit/button';
import { authenticateJira } from '@databases/connections/jira-auth';
import { messages } from '@databases/intl/messages';
import { useCallbackEffect } from '@databases/react';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { type JiraIssueRef } from '@databases/types/confluence';
import { JiraAuthenticationError } from '@databases/utils/confluence/jira';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import {
  selectJiraIssueOptional,
  selectJiraIssueMetadata,
} from '@databases/utils/redux/jira/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { deserializeError } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import AbstractLink from './AbstractLink';
import JiraIssueIcon from './JiraIssueIcon';
import JiraIssueLabel from './JiraIssueLabel';
import JiraIssueStatus from './JiraIssueStatus';

export interface JiraIssueLinkProps {
  issue: JiraIssueRef;
  useSmartLink: boolean;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  isReadonly?: boolean;
}

/**
 * Renders a formatted placeholder representing a Jira issue. The jira issue is resolved automatically to retrieve
 * the issue URL, summary and status. Until the issue is resolved, the issue key is shown as plain text, once it
 * is resolved, it is converted into a link to the issue.
 */
const JiraIssueLink: FunctionComponent<JiraIssueLinkProps> = ({
  issue,
  noFocus,
  onFocus,
  useSmartLink,
}) => {
  const { formatMessage } = useIntl();
  const [baseUrl, resolvedIssue, authenticationPath] =
    defaultStoreUtils.useShallowEqualSelector((state) => {
      const status = selectJiraIssueMetadata(state, issue);
      const error =
        status?.status === AsyncOperationStatus.ERROR
          ? deserializeError(status.error)
          : undefined;
      return [
        selectBaseUrl(state),
        selectJiraIssueOptional(state, issue),
        error instanceof JiraAuthenticationError ? error.authenticationPath : undefined,
      ] as const;
    });

  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();

  useCallbackEffect(
    useCallback(async () => {
      try {
        await dispatch(loadJiraIssue(issue));
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (e) {
        if (!(e instanceof JiraAuthenticationError)) {
          throw e;
        }
      }
      // Reload issues also after authentication
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, issue, authenticationPath])
  );

  const handleAuthenticate = useCallback(
    async (e: React.MouseEvent) => {
      if (!isModifierKeyPressed(e)) {
        e.preventDefault();

        await authenticateJira(
          issue.cloudId,
          issue.serverId,
          authenticationPath!,
          store,
          formatMessage(messages['snackbar-error.enable-popup'])
        );
      }
    },
    [authenticationPath, formatMessage, issue.cloudId, issue.serverId, store]
  );

  if (authenticationPath) {
    return (
      <AbstractLink>
        <JiraIssueLabel issue={issue} />
        {
          // Won't translate parentheses
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        }
        (
        <Button
          appearance="link"
          spacing="none"
          href={`${baseUrl}${authenticationPath}`}
          target="_blank"
          onClick={handleAuthenticate}
        >
          {formatMessage(messages['field-settings.type-link-jira-authenticate-text'])}
        </Button>
        {
          // Won't translate parentheses
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        }
        )
      </AbstractLink>
    );
  } else {
    return (
      <AbstractLink
        href={resolvedIssue?.url}
        useSmartLink={useSmartLink}
        onFocus={onFocus}
        noFocus={noFocus}
        iconBefore={<JiraIssueIcon issue={issue} />}
        iconAfter={resolvedIssue ? <JiraIssueStatus issue={issue} /> : undefined}
      >
        <JiraIssueLabel issue={issue} />
      </AbstractLink>
    );
  }
};

export default JiraIssueLink;
